import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Plus, Pencil, Trash2, X, Check, ShieldCheck, LogOut, Eye, EyeOff, ChevronUp, ChevronDown, Upload } from "lucide-react";
import { FabricModal, SiteLogin } from "./NewArrivals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgUrl = (id) => `${API}/site/fabric-images/${id}`;
const SITE_KEY = "nayara_site_token";
const authHeaders = () => { const t = localStorage.getItem(SITE_KEY); return t ? { Authorization: `Bearer ${t}` } : {}; };
const WHATSAPP = "919597888978";
const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
const EASE = [0.22, 1, 0.36, 1];
const LINEN = "repeating-linear-gradient(0deg,rgba(139,107,58,0.035) 0 1px,transparent 1px 4px)";
const FOLDS = "repeating-linear-gradient(0deg,transparent 0 34px,rgba(255,255,255,0.22) 34px 36px,rgba(0,0,0,0.18) 36px 39px)";
const zigBottom = (n, d) => {
  const p = ["0 0", "100% 0", `100% calc(100% - ${d}px)`];
  for (let i = n - 1; i >= 0; i--) p.push(`${((i + 0.5) / n) * 100}% 100%`, `${(i / n) * 100}% calc(100% - ${d}px)`);
  return `polygon(${p.join(",")})`;
};
const zigSides = (n = 3, d = 5) => {
  const p = ["0 0", "100% 0"];
  for (let i = 0; i < n; i++) p.push(`calc(100% - ${d}px) ${((i + 0.5) / n) * 100}%`, `100% ${((i + 1) / n) * 100}%`);
  p.push("0 100%");
  for (let i = n - 1; i >= 0; i--) p.push(`${d}px ${((i + 0.5) / n) * 100}%`, `0 ${(i / n) * 100}%`);
  return `polygon(${p.join(",")})`;
};
const TAG = zigSides(3, 5);
const up = (d = 0) => ({ initial: { opacity: 0, y: 26 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-40px" }, transition: { delay: d, duration: 0.7, ease: EASE } });
const ICON_BTN = "grid h-9 w-9 place-items-center border border-dashed border-[#8B6B3A]/60 text-[#2A2220] transition-colors hover:bg-[#2A2220] hover:text-[#F4E4BC]";

function Awning() {
  return (
    <div aria-hidden>
      <div className="h-8 shadow-[inset_0_-6px_10px_rgba(0,0,0,0.12)]" style={{ background: "repeating-linear-gradient(90deg,#8B6B3A 0 60px,#F4E4BC 60px 120px)" }} />
      <div className="flex h-7 overflow-hidden">
        {Array.from({ length: 40 }).map((_, i) => (
          <span key={i} className="h-7 w-[60px] flex-none rounded-b-[30px] shadow-[0_6px_8px_rgba(0,0,0,0.10)]" style={{ background: i % 2 ? "#F4E4BC" : "#8B6B3A" }} />
        ))}
      </div>
    </div>
  );
}

function Hanger({ className = "" }) {
  return (
    <svg className={className} width="56" height="30" viewBox="0 0 56 30" fill="none" stroke="#8B6B3A" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
      <path d="M28 10a4 4 0 1 1 4-4" /><path d="M28 10v2L4 27h48L28 12" />
    </svg>
  );
}

function PriceTag({ price, i, reduce }) {
  return (
    <motion.div className="absolute -top-6 right-3 flex flex-col items-center" style={{ transformOrigin: "50% 0%", filter: "drop-shadow(0 6px 8px rgba(0,0,0,0.22))" }}
      animate={reduce ? {} : { rotate: [-4, 3, -4] }} transition={{ duration: 3 + (i % 4) * 0.4, repeat: Infinity, ease: "easeInOut" }}>
      <span className="h-5 w-px bg-[#8B6B3A]" />
      <span className="font-display relative bg-[#F4E4BC] px-3.5 pb-1.5 pt-3 text-xl leading-none text-[#3a2a1a]" style={{ clipPath: TAG }}>
        <span className="absolute left-1/2 top-1 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-[#2A2220]" />
        {inr(price)}<small className="ml-0.5 font-sans text-[9px] text-[#8B6B3A]">/m</small>
      </span>
    </motion.div>
  );
}

function FabricBolt({ f, i, isAdmin, onEdit, onDelete, reduce }) {
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ delay: (i % 6) * 0.06, duration: 0.6, ease: EASE }} className="group relative w-[200px] flex-none sm:w-[210px]" data-testid={`store-fabric-${f.id}`}>
      <div className="relative h-[260px] overflow-hidden bg-[#EFE4D4] shadow-[0_14px_22px_rgba(0,0,0,0.18)]" style={{ clipPath: zigBottom(9, 9) }}>
        <img src={imgUrl(f.images?.[0])} alt={f.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
        <div className="pointer-events-none absolute inset-0 mix-blend-soft-light" style={{ background: FOLDS }} />
        <span className="pointer-events-none absolute inset-x-2 bottom-4 top-2 border border-dashed border-white/55" />
        {f.is_new && <span className="absolute -left-8 top-4 -rotate-[40deg] bg-[#C9A96E] px-9 py-1 text-[9px] uppercase tracking-[0.3em] text-[#1A1410]">New</span>}
        {isAdmin ? (
          <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-2">
            <button aria-label="Edit fabric" onClick={() => onEdit(f)} className="grid h-10 w-10 place-items-center rounded-full bg-[#FBF7F1] text-[#2B1A10] hover:bg-[#2B1A10] hover:text-[#FBF7F1]"><Pencil className="h-4 w-4" /></button>
            <button aria-label="Delete fabric" onClick={() => onDelete(f)} className="grid h-10 w-10 place-items-center rounded-full bg-[#FBF7F1] text-[#9B2F1E] hover:bg-[#9B2F1E] hover:text-white"><Trash2 className="h-4 w-4" /></button>
          </div>
        ) : (
          <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hi, I'm interested in ${f.name} (${f.category})`)}`} target="_blank" rel="noreferrer"
            className="absolute inset-x-3 bottom-6 translate-y-3 bg-[#FBF7F1] py-2.5 text-center text-[10px] uppercase tracking-[0.22em] text-[#2B1A10] opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">Enquire on WhatsApp</a>
        )}
      </div>
      {f.price > 0 && <PriceTag price={f.price} i={i} reduce={reduce} />}
      <p className="font-display mt-4 text-2xl font-light leading-tight text-[#2B1A10]">{f.name}</p>
      <p className="mt-0.5 text-[10px] uppercase tracking-[0.22em] text-[#8B6B3A]">{f.category}</p>
    </motion.div>
  );
}

function AddCard({ title, onClick }) {
  return (
    <button onClick={onClick} className="group flex h-[260px] w-[200px] flex-none flex-col items-center justify-center border-2 border-dashed border-[#8B6B3A]/55 bg-[#C9A96E]/[0.08] text-center text-[#5a3d22] transition-colors hover:bg-[#C9A96E]/15" data-testid="store-add-fabric">
      <Hanger />
      <span className="mt-3 grid h-12 w-12 place-items-center rounded-full bg-[#2A2220] text-[#F4E4BC] transition-transform duration-500 group-hover:rotate-90"><Plus className="h-5 w-5" /></span>
      <span className="font-display mt-3 px-3 text-2xl leading-tight">Add to {title}</span>
      <span className="mt-1 text-[10px] uppercase tracking-[0.14em] text-[#8B6B3A]">Photo · name · price</span>
    </button>
  );
}

function Rack({ r, idx, items, isAdmin, reduce, onAdd, onEdit, onDelete, onEditRack, onToggle, onMove, onDeleteRack }) {
  return (
    <section id={`rack-${r.id}`} className={`mx-auto mt-16 max-w-[1400px] px-6 lg:px-12 ${r.hidden ? "opacity-50" : ""}`} data-testid={`rack-${r.name}`}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <motion.div {...up(0)} className="relative flex items-center gap-3 bg-gradient-to-br from-[#d9bf8c] via-[#b8955e] to-[#e6cf9f] py-3 pl-7 pr-8 text-[#2A2220] shadow-[0_6px_12px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.5)]">
          <span className="absolute left-2 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-[#7a5a30]" />
          <span className="absolute right-2 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-[#7a5a30]" />
          {r.cover && <img src={imgUrl(r.cover)} alt="" className="h-11 w-11 rounded-full object-cover ring-2 ring-[#2A2220]/20" />}
          <div>
            <p className="text-[9px] uppercase tracking-[0.4em] opacity-75">Rack {String(idx + 1).padStart(2, "0")}{r.hidden ? " · hidden" : ""}</p>
            <h2 className="font-display text-3xl font-light leading-tight">{r.title}</h2>
          </div>
        </motion.div>
        {r.description && <p className="max-w-md text-sm leading-relaxed text-[#6b625a]">{r.description}</p>}
        <span className="ml-auto text-[11px] uppercase tracking-[0.24em] text-[#8B6B3A]">{items.length} design{items.length === 1 ? "" : "s"}</span>
        {isAdmin && (
          <div className="flex items-center gap-1.5">
            <button aria-label="Move rack up" onClick={() => onMove(r, -1)} className={ICON_BTN}><ChevronUp className="h-4 w-4" /></button>
            <button aria-label="Move rack down" onClick={() => onMove(r, 1)} className={ICON_BTN}><ChevronDown className="h-4 w-4" /></button>
            <button aria-label={r.hidden ? "Show rack" : "Hide rack"} onClick={() => onToggle(r)} className={ICON_BTN}>{r.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
            <button aria-label="Edit rack" onClick={() => onEditRack(r)} className={ICON_BTN}><Pencil className="h-4 w-4" /></button>
            <button aria-label="Delete rack" onClick={() => onDeleteRack(r)} className={`${ICON_BTN} !text-[#9B2F1E]`}><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>
      <div className="relative">
        <div className="flex items-end gap-7 overflow-x-auto px-4 pb-6 pt-12">
          {items.map((f, i) => <FabricBolt key={f.id} f={f} i={i} isAdmin={isAdmin} reduce={reduce} onEdit={onEdit} onDelete={onDelete} />)}
          {isAdmin && <AddCard title={r.title} onClick={() => onAdd(r)} />}
          {isAdmin && items.length === 0 && <p className="self-center text-sm italic text-[#8B6B3A]">Empty rack — add the first fabric.</p>}
        </div>
        <div className="h-[22px] rounded-[3px] bg-gradient-to-b from-[#a8804c] via-[#7a5530] to-[#5e3f22] shadow-[0_14px_20px_rgba(0,0,0,0.18),inset_0_2px_0_rgba(255,255,255,0.25)]" />
        <div className="flex justify-between px-10" aria-hidden>
          <span className="h-5 w-4 bg-[#5e3f22] [clip-path:polygon(0_0,100%_0,0_100%)]" />
          <span className="h-5 w-4 bg-[#5e3f22] [clip-path:polygon(0_0,100%_0,100%_100%)]" />
        </div>
      </div>
    </section>
  );
}

function RackModal({ rack, onClose, onSaved }) {
  const edit = !!rack;
  const [title, setTitle] = useState(rack?.title || "");
  const [description, setDescription] = useState(rack?.description || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : rack?.cover ? imgUrl(rack.cover) : null), [file, rack]);
  const save = async () => {
    setErr("");
    if (!title.trim()) return setErr("Rack name venum");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim()); fd.append("description", description.trim());
      if (file) fd.append("cover", file);
      if (edit) await axios.put(`${API}/site/racks/${rack.id}`, fd, { headers: authHeaders() });
      else await axios.post(`${API}/site/racks`, fd, { headers: authHeaders() });
      onSaved();
    } catch (e) { setErr(e.response?.data?.detail || "Save aagala, thirumba try pannunga"); } finally { setBusy(false); }
  };
  const field = "h-[50px] border border-[#7B4520]/30 bg-white px-4 text-base normal-case tracking-normal text-[#2B1A10] outline-none focus:border-[#7B4520]";
  return (
    <motion.div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#2B1A10]/55 p-4 backdrop-blur-md" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div role="dialog" aria-label={edit ? "Edit rack" : "New rack"} onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30 }} transition={{ duration: 0.5, ease: EASE }}
        className="w-full max-w-[560px] bg-[#FBF7F1] text-[#3A2416] shadow-[0_60px_120px_-40px_rgba(0,0,0,0.6)]">
        <div className="flex items-center justify-between border-b border-[#7B4520]/15 px-8 pb-5 pt-7">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.36em] text-[#7B4520]">{edit ? "Edit rack" : "Open a new rack"}</div>
            <div className="font-display mt-1 text-4xl font-light text-[#2B1A10]">{edit ? rack.title : "New collection"}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="grid h-11 w-11 place-items-center rounded-full border border-[#7B4520]/30"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex flex-col gap-5 px-8 py-7">
          <label className="flex flex-col gap-2.5 text-[11px] uppercase tracking-[0.22em] text-[#5E4634]">Rack / collection name
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Wedding Backdrops" className={field} /></label>
          <label className="flex flex-col gap-2.5 text-[11px] uppercase tracking-[0.22em] text-[#5E4634]">Short description
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="What's on this rack?" className={`${field} h-auto py-3`} /></label>
          <div className="flex items-center gap-4">
            <label className="flex h-[88px] w-[88px] cursor-pointer flex-col items-center justify-center gap-1 overflow-hidden rounded-full border-[1.5px] border-dashed border-[#7B4520]/50 bg-[#F3EADD] text-[10px] text-[#7B4520]">
              {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : <><Upload className="h-5 w-5" strokeWidth={1.4} />Cover</>}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <p className="text-xs leading-relaxed text-[#8A6E58]">Optional cover photo — shows on the rack's brass name-plate.</p>
          </div>
          {err && <div className="border border-[#9B2F1E]/30 bg-[#9B2F1E]/5 px-4 py-3 text-sm text-[#9B2F1E]">{err}</div>}
        </div>
        <div className="flex justify-end gap-3 border-t border-[#7B4520]/15 px-8 py-6">
          <button onClick={onClose} className="border border-[#7B4520]/35 px-6 py-3.5 text-xs font-medium uppercase tracking-[0.18em]">Cancel</button>
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-[#7B4520] px-7 py-3.5 text-xs font-medium uppercase tracking-[0.18em] text-[#FBF7F1] disabled:opacity-60">
            <Check className="h-4 w-4" />{busy ? "Saving..." : edit ? "Save rack" : "Open rack"}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function FabricStore() {
  const reduce = useReducedMotion();
  const hasAdminParam = new URLSearchParams(window.location.search).has("admin");
  const [racks, setRacks] = useState(null);
  const [fabrics, setFabrics] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filter, setFilter] = useState("all");
  const [fabModal, setFabModal] = useState(null);
  const [rackModal, setRackModal] = useState(null);
  const [showLogin, setShowLogin] = useState(() => hasAdminParam && !localStorage.getItem(SITE_KEY));

  const load = (admin) => {
    axios.get(`${API}/site/racks`, { params: admin ? { all: 1 } : {} }).then((r) => setRacks(r.data || [])).catch(() => setRacks([]));
    axios.get(`${API}/site/fabrics`, { params: { kind: "fabric", limit: 500 } }).then((r) => setFabrics(r.data || [])).catch(() => setFabrics([]));
  };
  const checkAdmin = () => {
    if (!localStorage.getItem(SITE_KEY) || !hasAdminParam) { setIsAdmin(false); load(false); return; }
    axios.get(`${API}/site/admin/me`, { headers: authHeaders() })
      .then(() => { setIsAdmin(true); setShowLogin(false); load(true); })
      .catch(() => { localStorage.removeItem(SITE_KEY); setIsAdmin(false); load(false); });
  };
  useEffect(() => { checkAdmin(); }, []); // eslint-disable-line
  const logout = () => { localStorage.removeItem(SITE_KEY); setIsAdmin(false); load(false); };

  const byRack = useMemo(() => {
    const m = {};
    fabrics.forEach((f) => { if (!m[f.category]) m[f.category] = []; m[f.category].push(f); });
    return m;
  }, [fabrics]);
  const all = racks || [];
  const visible = all.filter((r) => isAdmin || (!r.hidden && (byRack[r.name] || []).length > 0));
  const shown = filter === "all" ? visible : visible.filter((r) => r.id === filter);
  const catsFor = (name) => [name, ...all.map((x) => x.name).filter((n) => n !== name)];

  const act = async (fn) => { try { await fn(); load(true); } catch (e) { alert(e.response?.data?.detail || "Action failed"); } };
  const move = (r, dir) => {
    const ids = all.map((x) => x.id); const i = ids.indexOf(r.id); const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    act(() => axios.put(`${API}/site/racks/reorder`, { ids }, { headers: authHeaders() }));
  };
  const toggle = (r) => act(() => { const fd = new FormData(); fd.append("hidden", String(!r.hidden)); return axios.put(`${API}/site/racks/${r.id}`, fd, { headers: authHeaders() }); });
  const delRack = (r) => window.confirm(`Delete rack "${r.title}"? Fabrics inside will stay saved but won't show until moved to another rack.`) && act(() => axios.delete(`${API}/site/racks/${r.id}`, { headers: authHeaders() }));
  const delFabric = (f) => window.confirm(`Delete "${f.name}" from the website?`) && act(() => axios.delete(`${API}/site/fabrics/${f.id}`, { headers: authHeaders() }));
  const K = (cats) => ({ cats, add: "Add new fabric", noun: "fabric" });

  return (
    <div className="relative overflow-hidden bg-[#FBF8F2] pb-24 text-[#1A1A1A]" style={{ backgroundImage: LINEN }} data-testid="collections-page">
      <Awning />
      {isAdmin && (
        <div className="mx-auto mt-6 flex max-w-[1400px] flex-wrap items-center justify-between gap-3 bg-[#2A2220] px-6 py-3 text-[11px] uppercase tracking-[0.26em] text-[#F4E4BC] lg:mx-12 xl:mx-auto">
          <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#C9A96E]" />Admin mode · Fabric store</span>
          <div className="flex items-center gap-4">
            <button onClick={() => setRackModal({})} className="bg-[#C9A96E] px-5 py-2.5 tracking-[0.2em] text-[#1A1410]" style={{ clipPath: TAG }} data-testid="store-add-rack">+ Add collection</button>
            <button onClick={logout} className="flex items-center gap-2 hover:text-white"><LogOut className="h-4 w-4" />Logout</button>
          </div>
        </div>
      )}

      <div className="mx-auto grid max-w-[1400px] items-center gap-10 px-6 pt-14 lg:grid-cols-[1.3fr_1fr] lg:px-12">
        <motion.div {...up(0)}>
          <p className="flex items-center gap-4 text-[11px] uppercase tracking-[0.34em] text-[#8B6B3A]"><span className="h-px w-10 bg-[#8B6B3A]" />The Fabric Store</p>
          <h1 className="font-display mt-5 text-5xl font-light leading-[1.03] sm:text-6xl lg:text-7xl">Fabrics &amp; backdrops<br />for <em className="text-[#8B6B3A]">every story.</em></h1>
          <p className="mt-5 max-w-md text-[15px] leading-relaxed text-[#6b625a]">Browse our racks — every bolt is printed in-house in Tiruppur, cut to your size and finished by hand.</p>
        </motion.div>
        <motion.div className="hidden justify-self-end lg:block" style={{ transformOrigin: "50% -60px" }}
          animate={reduce ? {} : { rotate: [2, -1.5, 2] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} aria-hidden>
          <div className="mx-auto flex h-14 w-[250px] justify-between"><i className="h-14 w-px bg-[#8B6B3A]" /><i className="h-14 w-px bg-[#8B6B3A]" /></div>
          <div className="w-[330px] border-[6px] border-[#6b4a2a] bg-[#2A2220] px-5 pb-5 pt-6 text-center text-[#F4E4BC] shadow-[0_18px_30px_rgba(0,0,0,0.25),inset_0_0_0_1px_rgba(201,169,110,0.4)]">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#C9A96E]">Open daily</p>
            <p className="font-display mt-1.5 text-4xl font-light italic">Nayara Fabric Store</p>
            <p className="mt-2 text-[11px] tracking-[0.2em] text-[#F4E4BC]/70">PRINTED · CUT · FINISHED</p>
          </div>
        </motion.div>
      </div>

      <div className="mx-auto mt-12 flex max-w-[1400px] flex-wrap gap-2.5 border-b-[1.5px] border-dashed border-[#8B6B3A]/35 px-6 pb-6 lg:px-12">
        {[{ id: "all", title: "All racks" }, ...visible].map((r) => (
          <button key={r.id} onClick={() => setFilter(r.id)} style={{ clipPath: TAG }}
            className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.14em] transition-colors ${filter === r.id ? "bg-[#2A2220] text-[#F4E4BC]" : "bg-[#F1E9DC] text-[#5a4a3a] hover:bg-[#E9DCC8]"}`}>{r.title}</button>
        ))}
      </div>

      {racks === null && <div className="mx-auto mt-16 h-[320px] max-w-[1300px] animate-pulse bg-[#EFE4D4]" />}
      {shown.map((r) => (
        <Rack key={r.id} r={r} idx={all.indexOf(r)} items={byRack[r.name] || []} isAdmin={isAdmin} reduce={reduce}
          onAdd={(rk) => setFabModal({ mode: "add", cats: catsFor(rk.name) })}
          onEdit={(f) => setFabModal({ mode: "edit", item: f, cats: catsFor(f.category) })}
          onDelete={delFabric} onEditRack={(rk) => setRackModal({ rack: rk })} onToggle={toggle} onMove={move} onDeleteRack={delRack} />
      ))}
      {racks && !isAdmin && visible.length === 0 && (
        <p className="font-display mx-auto mt-20 max-w-[1400px] px-6 text-center text-3xl font-light text-[#8B6B3A]">Our racks are being restocked — check back soon.</p>
      )}

      {isAdmin && (
        <button onClick={() => setRackModal({})} className="group mx-6 mt-16 flex w-[calc(100%-3rem)] max-w-[1400px] items-center gap-6 border-2 border-dashed border-[#8B6B3A]/55 bg-[#C9A96E]/[0.07] p-8 text-left transition-colors hover:bg-[#C9A96E]/15 lg:mx-auto lg:w-[calc(100%-6rem)] xl:w-full">
          <span className="grid h-16 w-16 flex-none place-items-center rounded-full bg-[#2A2220] text-[#F4E4BC] transition-transform duration-500 group-hover:rotate-90"><Plus className="h-7 w-7" /></span>
          <span><span className="font-display block text-4xl font-light text-[#2A2220]">Open a new rack</span>
            <span className="mt-1 block text-sm text-[#6b625a]">Create a collection (name, cover photo, description) — then add fabrics to it.</span></span>
        </button>
      )}

      <AnimatePresence>
        {fabModal && <FabricModal key="fm" kind="fabric" K={K(fabModal.cats)} modal={fabModal} onClose={() => setFabModal(null)} onSaved={() => { setFabModal(null); load(true); }} />}
        {rackModal && <RackModal key="rm" rack={rackModal.rack} onClose={() => setRackModal(null)} onSaved={() => { setRackModal(null); load(true); }} />}
        {showLogin && !isAdmin && <SiteLogin key="sl" onClose={() => setShowLogin(false)} onDone={checkAdmin} />}
      </AnimatePresence>
    </div>
  );
}
